import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { ADMIN_API_URL, CASHE_LENDER_ID, CREDIT_API_URL } from "../../constants";
import { hasPermission } from '../../utils/permissions';
import Header from "../Header";
import Footer from "../Footer";
import ErrorModal from "../common/ErrorModal";
import NoDataModal from "../common/NoDataModal";
import showToast from "../../utils/toast";

const mockUsers = ["Manager 1", "Manager 2", "Admin"];
const leadStatuses = ["New", "Contacted", "Unable to connect", "Interested", "Awaiting Customer Details", "Awaiting Lender Update", "Approved", "Disbursed", "Rejected", "Dropped Off", "Invalid", "Did not Apply", "Loan Closed", "Loan Active", "EMI Overdue"];
const finalLeadStatuses = ["Rejected", "Disbursed", "Dropped Off", "Invalid", "Did not Apply"];

const LeadsPage = ({ addNotification }) => {
    const { user } = useSelector(state => state.auth);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check if user has Quick Search permission (only for sub-users, not super_admin)
    const canUseQuickSearch = user?.role !== 'super_admin' && hasPermission(user, 'Quick Search');
    
    // Check if user is super admin (no tabs needed for super admin)
    const isSuperAdmin = user?.role === 'super_admin';
    
    // Check if coming back from search mode
    const shouldStartInSearchMode = new URLSearchParams(location.search).get('mode') === 'search';
    
    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [productType, setProductType] = useState("");
    const [loanAmount, setLoanAmount] = useState("");
    const [mobile, setMobile] = useState("");
    const [leadId, setLeadId] = useState("");
    const [assignedToFilter, setAssignedToFilter] = useState("");
    const [lenderId, setLenderId] = useState("");
    const [source, setSource] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10); // New state for records per page
    const scrollPositionRef = useRef(0);

    // Modal states
    const [statusModal, setStatusModal] = useState({ open: false, lead: null });
    const [noteModal, setNoteModal] = useState({ open: false, lead: null });
    const [noteText, setNoteText] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");

    // State for Remarks Modal
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [remarksToDisplay, setRemarksToDisplay] = useState('');
    const [currentRemarksLead, setCurrentRemarksLead] = useState(null);
    const [newRemarkText, setNewRemarkText] = useState('');

    // State to manage next button visibility
    const [hasMoreLeads, setHasMoreLeads] = useState(true);

    const [progressModal, setProgressModal] = useState({ open: false, steps: [], completed: [] });

    // State for customer state update
    const [updatingCustomerState, setUpdatingCustomerState] = useState({});

    // State for search mode toggle (initialize based on query parameter)
    const [isSearchMode, setIsSearchMode] = useState(shouldStartInSearchMode && canUseQuickSearch);
    const [hasSearched, setHasSearched] = useState(shouldStartInSearchMode && canUseQuickSearch);
    
    // State for multiple values in Quick Search
    const [mobileNumbers, setMobileNumbers] = useState([]);
    const [leadIds, setLeadIds] = useState([]);
    const [currentMobileInput, setCurrentMobileInput] = useState('');
    const [currentLeadIdInput, setCurrentLeadIdInput] = useState('');

    // Functions to handle multiple values
    const addMobileNumber = (mobile) => {
        // Sanitize the mobile number by removing all non-numeric characters
        const cleanMobile = mobile ? mobile.replace(/\D/g, '') : '';
        if (cleanMobile && cleanMobile.length === 10 && !mobileNumbers.includes(cleanMobile)) {
            setMobileNumbers(prev => [...prev, cleanMobile]);
            setCurrentMobileInput('');
        }
    };

    const removeMobileNumber = (mobile) => {
        setMobileNumbers(prev => prev.filter(m => m !== mobile));
    };

    const addLeadId = (leadId) => {
        // Sanitize the leadId by removing all whitespace characters
        const cleanLeadId = leadId ? leadId.replace(/\s/g, '').trim() : '';
        if (cleanLeadId && !leadIds.includes(cleanLeadId)) {
            setLeadIds(prev => [...prev, cleanLeadId]);
            setCurrentLeadIdInput('');
        }
    };

    const removeLeadId = (leadId) => {
        setLeadIds(prev => prev.filter(l => l !== leadId));
    };

    const handleMobileKeyDown = (e) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            addMobileNumber(currentMobileInput);
        } else if ((e.key === 'Backspace' || e.key === 'Delete') && currentMobileInput === '' && mobileNumbers.length > 0) {
            e.preventDefault();
            // Remove the last added mobile number
            const lastMobile = mobileNumbers[mobileNumbers.length - 1];
            removeMobileNumber(lastMobile);
        }
    };

    const handleLeadIdKeyDown = (e) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            addLeadId(currentLeadIdInput);
        } else if ((e.key === 'Backspace' || e.key === 'Delete') && currentLeadIdInput === '' && leadIds.length > 0) {
            e.preventDefault();
            // Remove the last added lead ID
            const lastLeadId = leadIds[leadIds.length - 1];
            removeLeadId(lastLeadId);
        }
    };

    // Handle query parameter and restore search results if coming back from search mode
    useEffect(() => {
        if (shouldStartInSearchMode && canUseQuickSearch) {
            // Try to restore previous search results from sessionStorage
            const savedSearchResults = sessionStorage.getItem('quickSearchResults');
            const savedSearchParams = sessionStorage.getItem('quickSearchParams');
            
            if (savedSearchResults && savedSearchParams) {
                try {
                    const parsedResults = JSON.parse(savedSearchResults);
                    const parsedParams = JSON.parse(savedSearchParams);
                    
                    // Restore leads data
                    setLeads(parsedResults);
                    
                    // Restore search parameters
                    if (parsedParams.mobileNumbers) setMobileNumbers(parsedParams.mobileNumbers);
                    if (parsedParams.leadIds) setLeadIds(parsedParams.leadIds);
                    if (parsedParams.mobile) setMobile(parsedParams.mobile);
                    if (parsedParams.leadId) setLeadId(parsedParams.leadId);
                } catch (error) {
                    console.error('Error restoring search results:', error);
                }
            }
            
            // Clear the query parameter from URL to avoid confusion
            navigate('/leads', { replace: true });
        }
    }, [shouldStartInSearchMode, canUseQuickSearch, navigate]);

    useEffect(() => {
        // Check for auth token in sessionStorage
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        // Only fetch leads automatically in normal mode
        if (!isSearchMode) {
        fetchLeads();
        }
        fetchUsers();
    }, [navigate, page, recordsPerPage, isSearchMode]);

    useEffect(() => {
        if (!loading && leads.length > 0 && scrollPositionRef.current !== 0) {
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0; // Reset after restoring
        }
    }, [loading, leads]);

    // Load background data (all leads without filters) for better UX
    const loadBackgroundData = async () => {
        // Do not load background data in Quick Search mode
        if (isSearchMode) return;
        const token = sessionStorage.getItem("authToken");
        if (!token) return;
        
        try {
            const response = await fetch("" + ADMIN_API_URL + "fetch-lead-data", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: 10,
                    startDate: "",
                    endDate: "",
                    productType: "",
                    loanAmount: "",
                    mobile: "",
                    leadId: "",
                    assignedTo: "",
                    lenderId: "",
                    sortBy: "createdAt",
                    sortOrder: "DESC"
                }),
            });

            const v_result = await response.json();
            
            if (response.ok && v_result.data) {
                // Handle both array and single object responses
                const dataArray = Array.isArray(v_result.data) ? v_result.data : [v_result.data];
                if (dataArray.length > 0) {
                    setLeads(dataArray);
                    setHasMoreLeads(Array.isArray(v_result.data) ? v_result.data.length === 10 : false);
                }
            }
        } catch (err) {
            // Silently fail for background data loading
            console.log("Background data loading failed:", err.message);
        }
    };

    // Fetch leads data
    const fetchLeads = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        scrollPositionRef.current = window.scrollY; // Capture scroll position before fetching
        setLoading(true);
        try {
            const response = await fetch("" + ADMIN_API_URL + "fetch-lead-data", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: (page - 1) * recordsPerPage,
                    maxLimit: recordsPerPage,
                    startDate: startDate ? formatDate(startDate) : "",
                    endDate: endDate ? formatDate(endDate) : "",
                    productType,
                    loanAmount,
                    mobile: mobileNumbers.length > 1 ? mobileNumbers : 
                            mobileNumbers.length === 1 ? mobileNumbers[0] : 
                            (currentMobileInput && currentMobileInput.length === 10) ? currentMobileInput : mobile,
                    leadId: leadIds.length > 1 ? leadIds : 
                             leadIds.length === 1 ? leadIds[0] : 
                             (currentLeadIdInput && currentLeadIdInput.trim() !== '') ? currentLeadIdInput : leadId,
                    assignedTo: assignedToFilter,
                    lenderId,
                    source,
                    status,
                    sortBy: "createdAt",
                    sortOrder: "DESC",
                    // Add viewAllLeads parameter for Quick Search mode
                    ...(isSearchMode && { viewAllLeads: true })
                }),
            });

            const v_result = await response.json();
            
            // Check for token expiration in response
            if (v_result.message && handleTokenExpiration(v_result.message)) {
                return; // Exit early if token expired
            }
            
            if (!response.ok) {
                throw new Error(v_result.message || "Failed to fetch leads data");
            }

            // Handle successful response
            if (v_result.data) {
                // Check if data is an array or single object
                const dataArray = Array.isArray(v_result.data) ? v_result.data : [v_result.data];
                
                if (dataArray.length > 0) {
                    setLeads(dataArray);
                    setHasMoreLeads(Array.isArray(v_result.data) ? v_result.data.length === recordsPerPage : false);
                    setNoDataMessage(null);
                    setError(null);
                    
                    // Save search results to sessionStorage if in search mode
                    if (isSearchMode) {
                        sessionStorage.setItem('quickSearchResults', JSON.stringify(dataArray));
                        sessionStorage.setItem('quickSearchParams', JSON.stringify({
                            mobileNumbers,
                            leadIds,
                            mobile,
                            leadId
                        }));
                    }
                } else {
                    // Handle empty data
                    setHasMoreLeads(false);
                    setNoDataMessage(v_result.message || "No leads found within the specified criteria.");
                    setError(null);
                    if (page > 1) {
                        setPage(prevPage => prevPage - 1);
                    }
                    // Load background data (all leads) for better UX - only in normal mode
                    if (!isSearchMode) {
                    loadBackgroundData();
                    }
                }
            } else {
                // Handle no data response
                setHasMoreLeads(false);
                setNoDataMessage(v_result.message || "No leads found within the specified criteria.");
                setError(null);
                if (page > 1) {
                    setPage(prevPage => prevPage - 1);
                }
                // Load background data (all leads) for better UX - only in normal mode
                if (!isSearchMode) {
                loadBackgroundData();
                }
            }

        } catch (err) {
            // Check for token expiration first
            if (handleTokenExpiration(err)) {
                return; // Exit early if token expired
            }
            
            // Check if it's a "No data found" or "not found" message vs actual error
            if (err.message && (err.message.includes("No leads found") || err.message.includes("not found"))) {
                setNoDataMessage("No leads found within the specified criteria.");
                setError(null);
                setHasMoreLeads(false);
                setLeads([]);
                // Load background data (all leads) for better UX - only in normal mode
                if (!isSearchMode) {
                loadBackgroundData();
                }
            } else {
                setError(err.message);
                setLeads([]);
                setHasMoreLeads(false);
                setNoDataMessage(null);
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch users data
    const fetchUsers = async () => {
        console.log("Attempting to fetch users...");
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            console.error("Authorization token not found for user fetch.");
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}users`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Check for token expiration in error response
                if (errorData.message && handleTokenExpiration(errorData.message)) {
                    return; // Exit early if token expired
                }
                throw new Error(errorData.message || "Failed to fetch users.");
            }

            const result = await response.json();
            
            // Check for token expiration in successful response
            if (result.message && handleTokenExpiration(result.message)) {
                return; // Exit early if token expired
            }
            setUsers(result.data);
            console.log("Users fetched successfully:", result.data);
        } catch (err) {
            // Check for token expiration first
            if (handleTokenExpiration(err)) {
                return; // Exit early if token expired
            }
            
            addNotification(`Error fetching users: ${err.message}`, 'error');
            console.error("Error fetching users:", err);
            setUsers([]); // Clear users on error
        }
    };

    // Helper function to format the date as dd-mm-yyyy
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Helper function to format ISO date to YYYY-MM-DD for input type="date"
    const formatDateForInput = (isoDateString) => {
        if (!isoDateString) return '';

        let date = new Date(isoDateString);

        // If parsing as ISO 8601 fails, try parsing as DD-MM-YYYY
        if (isNaN(date.getTime())) {
            const parts = isoDateString.split('-');
            if (parts.length === 3) {
                // Assume DD-MM-YYYY format and construct YYYY-MM-DD for new Date()
                date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
        }

        if (isNaN(date.getTime())) return ''; // Handle invalid date strings after all attempts

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPage(1); // Reset to first page on new filter/submit
        
        // Add current input values if they exist (for both modes)
        if (currentMobileInput && currentMobileInput.length === 10) {
            addMobileNumber(currentMobileInput);
        }
        if (currentLeadIdInput) {
            addLeadId(currentLeadIdInput);
        }
        
        // In search mode, validate that at least mobile numbers or leadIds are provided
        if (isSearchMode) {
            // Check if we have any values to search (including current inputs)
            const hasMobileNumbers = mobileNumbers.length > 0 || (currentMobileInput && currentMobileInput.length === 10);
            const hasLeadIds = leadIds.length > 0 || (currentLeadIdInput && currentLeadIdInput.trim() !== '');
            
            if (!hasMobileNumbers && !hasLeadIds) {
                addNotification('Please enter either Mobile Number or Lead ID', 'error');
                return;
            }
        }
        
        setHasSearched(true);
        fetchLeads();
    };

    // Add clear filter function
    const handleClearFilter = async () => {
        // Clear all filters
        setStartDate("");
        setEndDate("");
        setProductType("");
        setLoanAmount("");
        setMobile("");
        setLeadId("");
        setAssignedToFilter("");
        setLenderId("");
        setSource("");
        setStatus("");
        setPage(1);
        setHasSearched(false);
        
        // Clear multiple values in search mode
        setMobileNumbers([]);
        setLeadIds([]);
        setCurrentMobileInput('');
        setCurrentLeadIdInput('');
        
        // In search mode, just clear the data and don't fetch
        if (isSearchMode) {
            setLeads([]);
            setError(null);
            setNoDataMessage(null);
            // Clear saved search results from sessionStorage
            sessionStorage.removeItem('quickSearchResults');
            sessionStorage.removeItem('quickSearchParams');
            return;
        }
        
        // Manually call API with cleared filters instead of using fetchLeads()
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch("" + ADMIN_API_URL + "fetch-lead-data", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: recordsPerPage,
                    startDate: "",
                    endDate: "",
                    productType: "",
                    loanAmount: "",
                    mobile: "",
                    leadId: "",
                    assignedTo: "",
                    lenderId: "",
                    sortBy: "createdAt",
                    sortOrder: "DESC"
                }),
            });

            const v_result = await response.json();
            
            if (!response.ok) {
                throw new Error(v_result.message || "Failed to fetch leads data");
            }

            // Handle successful response
            if (v_result.data) {
                // Handle both array and single object responses
                const dataArray = Array.isArray(v_result.data) ? v_result.data : [v_result.data];
                if (dataArray.length > 0) {
                    setLeads(dataArray);
                    setHasMoreLeads(Array.isArray(v_result.data) ? v_result.data.length === recordsPerPage : false);
                    setNoDataMessage(null);
                    setError(null);
                } else {
                    setLeads([]);
                    setHasMoreLeads(false);
                    setNoDataMessage(null);
                    setError(null);
                }
            } else {
                setLeads([]);
                setHasMoreLeads(false);
                setNoDataMessage(null);
                setError(null);
            }
        } catch (err) {
            setError(err.message);
            setLeads([]);
            setHasMoreLeads(false);
            setNoDataMessage(null);
        } finally {
            setLoading(false);
        }
    };

    // Function to update lead data in the backend
    const updateLeadData = async (leadId, updatedFields) => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}update-lead-data/${leadId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify(updatedFields),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update lead data.");
            }

            const result = await response.json();
            addNotification(result.message || "Lead data updated successfully.", 'success');
            return result.data;

        } catch (error) {
            addNotification(`Error updating lead: ${error.message}`, 'error');
            console.error("Error updating lead data:", error);
            return null;
        }
    };

    // Function to fetch all leads without pagination
    const fetchAllLeads = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            return null;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}fetch-lead-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: 999999, // A very large number to get all records
                    startDate: startDate ? formatDate(startDate) : "",
                    endDate: endDate ? formatDate(endDate) : "",
                    productType,
                    loanAmount,
                    // Mirror main fetchLeads logic for mobiles
                    mobile: mobileNumbers.length > 1 ? mobileNumbers :
                            mobileNumbers.length === 1 ? mobileNumbers[0] :
                            (currentMobileInput && currentMobileInput.length === 10) ? currentMobileInput : mobile,
                    // Mirror main fetchLeads logic for leadIds
                    leadId: leadIds.length > 1 ? leadIds :
                             leadIds.length === 1 ? leadIds[0] :
                             (currentLeadIdInput && currentLeadIdInput.trim() !== '') ? currentLeadIdInput : leadId,
                    assignedTo: assignedToFilter,
                    lenderId,
                    source,
                    status,
                    sortBy: "createdAt",
                    sortOrder: "DESC",
                    // Send viewAllLeads only in Quick Search mode
                    ...(isSearchMode && { viewAllLeads: true })
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch all leads data");
            }

            const result = await response.json();
            // Handle both array and single object responses
            if (result.data) {
                return Array.isArray(result.data) ? result.data : [result.data];
            }
            return [];
        } catch (err) {
            addNotification(`Error fetching all leads: ${err.message}`, 'error');
            return null;
        }
    };

    // Function to generate CSV from leads data
    const generateCSV = async () => {
        // Show loading state
        setLoading(true);
        
        // Fetch all leads
        const allLeads = await fetchAllLeads();
        
        if (!allLeads) {
            setLoading(false);
            return;
        }

        const csvRows = [];
        const headers = Object.keys(allLeads[0] || {});
        csvRows.push(headers.join(','));
        
        allLeads.forEach(lead => {
            const row = headers.map(field => {
                let value = lead[field] || '';
                if (typeof value === 'object') {
                    if (field === 'applicantDetails') value = value.name;
                    else if (Array.isArray(value)) value = value.map(item => item.name || item.text || item).join('; ');
                    else value = JSON.stringify(value);
                }
                return `"${value}"`;
            });
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'leads_and_applications.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        
        // Hide loading state
        setLoading(false);
    };

    // Handler to open Lead Form Modal
    const handleGenerateLead = () => {
        navigate('/generate-lead'); // Navigate to the new dedicated page
    };

    // Handle direct assignment change
    const handleAssignmentChange = async (leadId, newAssignedTo) => {
        const updatedLead = await updateLeadData(leadId, { assignedTo: newAssignedTo });

        if (updatedLead) {
            setLeads(leads.map(l => l.id === leadId ? { ...l, assignedTo: updatedLead.assignedTo } : l));
            const userName = getUserNameById(newAssignedTo);
            addNotification(`Lead #${leadId} assigned to ${userName}.`, 'newLead');
        } else {
            addNotification(`Failed to assign lead #${leadId}.`, 'error');
        }
    };

    // Change Status
    const handleChangeStatus = (lead) => {
        setSelectedStatus(lead.leadStatus || leadStatuses[0]);
        setStatusModal({ open: true, lead });
    };
    const handleStatusSave = () => {
        setLeads(leads.map(l => l.id === statusModal.lead.id ? { ...l, leadStatus: selectedStatus } : l));
        addNotification(`Lead #${statusModal.lead.id} status changed to ${selectedStatus}.`, 'statusChange');
        setStatusModal({ open: false, lead: null });
    };

    // Add Note
    const handleAddNote = (lead) => {
        setNoteText(""); // Clear noteText when opening for a new note
        setNoteModal({ open: true, lead });
    };
    const handleNoteSave = async () => {
        if (!noteText.trim()) {
            addNotification("Note cannot be empty.", 'error');
            return;
        }

        const currentRemarks = noteModal.lead.remarks || '';
        const timestamp = new Date().toLocaleString(); // Format date/time
        const newNoteEntry = `[${timestamp}] ${noteText.trim()}`;
        
        // Prepend new note to existing remarks, separated by a newline
        const updatedRemarks = currentRemarks ? `${newNoteEntry}\n${currentRemarks}` : newNoteEntry;

        const updatedLead = await updateLeadData(noteModal.lead.id, { remarks: updatedRemarks });

        if (updatedLead) {
            setLeads(leads.map(l => l.id === noteModal.lead.id ? { ...l, remarks: updatedLead.remarks } : l));
            addNotification(`Note added to lead #${noteModal.lead.id}.`, 'info');
        } else {
            addNotification(`Failed to add note to lead #${noteModal.lead.id}.`, 'error');
        }
        setNoteModal({ open: false, lead: null });
        setNoteText(''); // Clear the note input after saving or attempting to save
    };

    // Handle saving new remark from unified remarks modal
    const handleRemarksModalSave = async () => {
        if (!newRemarkText.trim()) {
            addNotification("Note cannot be empty.", 'error');
            return;
        }

        const currentRemarks = currentRemarksLead.remarks || '';
        const timestamp = new Date().toLocaleString(); // Format date/time
        const newNoteEntry = `[${timestamp}] ${newRemarkText.trim()}`;
        
        // Prepend new note to existing remarks, separated by a newline
        const updatedRemarks = currentRemarks ? `${newNoteEntry}\n${currentRemarks}` : newNoteEntry;

        const updatedLead = await updateLeadData(currentRemarksLead.id, { remarks: updatedRemarks });

        if (updatedLead) {
            setLeads(leads.map(l => l.id === currentRemarksLead.id ? { ...l, remarks: updatedLead.remarks } : l));
            addNotification(`Note added to lead #${currentRemarksLead.id}.`, 'info');
            
            // Update the displayed remarks and clear the input
            setRemarksToDisplay(updatedRemarks);
            setNewRemarkText('');
            setCurrentRemarksLead(prev => ({ ...prev, remarks: updatedRemarks }));
        } else {
            addNotification(`Failed to add note to lead #${currentRemarksLead.id}.`, 'error');
        }
    };

    // Handle Lead Status Change directly in table
    const handleLeadStatusChange = async (leadId, newStatus) => {
        const updatedLead = await updateLeadData(leadId, { leadStatus: newStatus });

        if (updatedLead) {
            setLeads(prevLeads => prevLeads.map(lead => 
                lead.id === leadId ? { 
                    ...lead, 
                    leadStatus: updatedLead.leadStatus,
                    isMobileVerified: updatedLead.isMobileVerified 
                } : lead
            ));
            addNotification(`Lead #${leadId} status changed to ${newStatus}.`, 'statusChange');
        } else {
            addNotification(`Failed to update lead #${leadId} status.`, 'error');
        }
    };

    // Handle Follow-up Date Change directly in table
    const handleFollowUpDateChange = async (leadId, newDate) => {
        const formattedDate = formatDate(newDate);
        const updatedLead = await updateLeadData(leadId, { followUpDate: formattedDate });

        if (updatedLead) {
            setLeads(prevLeads => prevLeads.map(lead => 
                lead.id === leadId ? { ...lead, followUpDate: updatedLead.followUpDate } : lead
            ));
            addNotification(`Lead #${leadId} follow-up date updated.`, 'info');
        } else {
            // Optionally, revert the local state or show an error if the update failed
            addNotification(`Failed to update lead #${leadId} follow-up date.`, 'error');
        }
    };

    // View Details
    const handleViewDetails = (lead) => {
        // Add query parameter to indicate if coming from Quick Search mode
        const queryParam = isSearchMode ? '?mode=search' : '';
        navigate(`/leads/${lead.id}${queryParam}`); // Navigate to the new dedicated page
    };

    // Add Underwriting Note
    const handleAddUnderwritingNoteToLead = (leadId, noteToAdd) => {
        const newNote = noteToAdd; // Re-declare to try and resolve ESLint no-undef issue
        setLeads(prevLeads => prevLeads.map(lead => 
            lead.id === leadId ? { 
                ...lead, 
                underwritingNotes: [...(lead.underwritingNotes || []), newNote] 
            } : lead
        ));
        // The selectedLeadForDetails state is no longer relevant for the modal, 
        // but this logic might be needed if you still use it elsewhere or want 
        // to update the lead details on the new page after adding a note. 
        // For now, I'm removing the conditional update for selectedLeadForDetails. 
        /*
        if (selectedLeadForDetails && selectedLeadForDetails.id === leadId) {
            setSelectedLeadForDetails(prevLead => ({
                ...prevLead,
                underwritingNotes: [...(prevLead.underwritingNotes || []), newNote]
            }));
        }
        */
    };

    // Helper function to check for token expiration
    const handleTokenExpiration = (error) => {
        if (error && error.name === "TokenExpiredError" || 
            (error.message && error.message.includes("jwt expired")) ||
            (typeof error === 'object' && error.message && error.message.name === "TokenExpiredError")) {
            showToast("Session expired. Please login again.", 'warning');
            addNotification("Session expired. Please login again.", 'warning');
            // Clear session storage
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("userInfo");
            // Redirect to login
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return true;
        }
        return false;
    };

    // Helper function to get user name by ID
    const getUserNameById = (userId) => {
        if (!userId) return 'N/A';
        const user = users.find(u => u.id === userId);
        return user ? (user.name || user.username || 'Unknown User') : 'Unknown User';
    };

    // Function to update customer state via lender API
    const updateCustomerState = async (lead) => {
        const lenderId = lead.lenderInfo?.lenderId;
        const partnerCustomerId = lead.partnerCustomerId;
        
        if (!lenderId) {
            addNotification("No lender assigned to this lead.", 'error');
            return;
        }
        
        if (!partnerCustomerId) {
            addNotification("No partner customer ID found for this lead.", 'error');
            return;
        }

        // Currently only supporting CASHe lender
        if (lenderId !== CASHE_LENDER_ID) {
            addNotification("Customer state update is currently only supported for CASHe lender.", 'info');
            return;
        }

        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }

        // Set loading state for this specific lead
        setUpdatingCustomerState(prev => ({ ...prev, [lead.id]: true }));

        try {
            const response = await fetch(`${CREDIT_API_URL}lender/customer-state`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    lenderId: "CASHe",
                    partnerCustomerId: partnerCustomerId
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update customer state.");
            }

            const result = await response.json();
            
            if (result.success) {
                addNotification("Customer state updated successfully. Refreshing lead data...", 'success');
                
                // Fetch updated lead data from backend only in normal mode
                if (!isSearchMode) {
                    await fetchLeads();
                }
            } else {
                addNotification("Customer state updated successfully.", 'success');
            }

        } catch (error) {
            addNotification(`Error updating customer state: ${error.message}`, 'error');
            console.error("Error updating customer state:", error);
        } finally {
            // Clear loading state for this specific lead
            setUpdatingCustomerState(prev => ({ ...prev, [lead.id]: false }));
        }
    };

    function getAppProgressSteps(lead) {
        if (!lead.chatHistory || !Array.isArray(lead.chatHistory)) return { steps: [], completed: [] };
        const history = lead.chatHistory;
        const steps = [
            { key: 'applicationType', label: 'Application Type', check: (h) => h.some(m => m.messageText?.includes('Selected Application Type')) },
            { key: 'panNumber', label: 'PAN Number', check: (h) => h.some(m => m.responseType === 'PAN' || m.messageText?.includes('PAN Number:')) },
            { key: 'panUpload', label: 'PAN Uploaded', check: (h) => h.some(m => m.messageText?.includes('PAN card document uploaded successfully')) },
            { key: 'aadharNumber', label: 'Aadhar Number', check: (h) => h.some(m => m.responseType === 'Aadhar' || m.messageText?.includes('Aadhar Number:')) },
            { key: 'aadharUpload', label: 'Aadhar Uploaded', check: (h) => h.some(m => m.messageText?.includes('Aadhar card document uploaded successfully')) },
            { key: 'bankUpload', label: 'Bank Uploaded', check: (h) => h.some(m => m.messageText?.includes('Bank statement uploaded successfully')) },
            { key: 'itrUpload', label: 'ITR Uploaded', check: (h) => h.some(m => m.messageText?.includes('ITR statement uploaded successfully')) },
        ];
        let relevantSteps = steps;
        if (!['COMMERCIAL VEHICLE LOAN', 'BUSINESS LOAN'].includes(lead.productType)) {
            relevantSteps = steps.slice(0, 2);
        }
        const completed = relevantSteps.filter(step => step.check(history)).map(s => s.key);
        return { steps: relevantSteps, completed };
    }

    function getAppProgress(lead) {
        if (!lead.chatHistory || !Array.isArray(lead.chatHistory)) return 'No Progress';
        const { steps, completed } = getAppProgressSteps(lead);
        if (completed.length === 0) return 'No Progress';
        if (completed.length === steps.length) return 'Complete';
        return `${completed.length}/${steps.length} steps done`;
    }

    if (loading) {
        return (
            <>
                <Header />
                <div id="wrapper" style={{ paddingTop: '70px' }}>
                    <div className="content">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="hpanel">
                                    <div className="panel-body text-center" style={{ padding: '50px' }}>
                                        <i className="fa fa-spinner fa-spin fa-3x text-muted"></i>
                                        <h4 className="m-t-md text-muted">Loading leads...</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    // Handle back to leads functionality
    const handleBackToLeads = async () => {
        setError(null);
        setLeads([]);
        setNoDataMessage(null);
        setHasSearched(false);
        
        // Reset all filters
        setStartDate("");
        setEndDate("");
        setProductType("");
        setLoanAmount("");
        setMobile("");
        setLeadId("");
        setAssignedToFilter("");
        setLenderId("");
        setSource("");
        setStatus("");
        setPage(1);
        
        // In search mode, don't fetch data automatically
        if (isSearchMode) {
            return;
        }
        
        // Fetch leads with cleared filters only in normal mode
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch("" + ADMIN_API_URL + "fetch-lead-data", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "authorization": `${token}`,
                },
                body: JSON.stringify({
                    minLimit: 0,
                    maxLimit: 10,
                    startDate: "",
                    endDate: "",
                    productType: "",
                    loanAmount: "",
                    mobile: "",
                    leadId: "",
                    assignedTo: "",
                    lenderId: "",
                    sortBy: "createdAt",
                    sortOrder: "DESC"
                }),
            });

            const v_result = await response.json();
            
            if (!response.ok) {
                throw new Error(v_result.message || "Failed to fetch leads data");
            }

            // Handle successful response
            if (v_result.data) {
                // Handle both array and single object responses
                const dataArray = Array.isArray(v_result.data) ? v_result.data : [v_result.data];
                if (dataArray.length > 0) {
                    setLeads(dataArray);
                    setHasMoreLeads(Array.isArray(v_result.data) ? v_result.data.length === 10 : false);
                    setNoDataMessage(null);
                    setError(null);
                }
            } else {
                // Handle empty data
                // Don't clear leads array to maintain background data
                setHasMoreLeads(false);
                setNoDataMessage(v_result.message || "No leads found within the specified criteria.");
                setError(null);
            }
        } catch (err) {
            // Check if it's a "No data found" or "not found" message vs actual error
            if (err.message && (err.message.includes("No leads found") || err.message.includes("not found"))) {
                setNoDataMessage("No leads found within the specified criteria.");
                setError(null);
                setHasMoreLeads(false);
                setLeads([]);
                // Load background data (all leads) for better UX - only in normal mode
                if (!isSearchMode) {
                loadBackgroundData();
                }
            } else {
                setError(err.message);
                setLeads([]);
                setHasMoreLeads(false);
                setNoDataMessage(null);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    <div style={{ marginBottom: '25px' }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            marginBottom: '15px'
                                        }}>
                                            <div>
                                                <h2 className="font-light" style={{ margin: '0 0 5px 0' }}>
                                                    Leads & Loan Applications
                                                </h2>
                                                <p className="text-muted" style={{ margin: 0, fontSize: '14px' }}>
                                                    {isSuperAdmin ? 'Manage and track all lead applications' : 
                                                     isSearchMode ? 'Search by Mobile Number or Lead ID to view application details' : 
                                                     'Manage and track all lead applications'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Show tabs only for non-super admin users */}
                                        {!isSuperAdmin && (
                                            <div style={{ 
                                                display: 'inline-flex', 
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '8px',
                                                padding: '4px',
                                                gap: '4px',
                                                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                                            }}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsSearchMode(false);
                                                    setHasSearched(false);
                                                    setStartDate("");
                                                    setEndDate("");
                                                    setProductType("");
                                                    setLoanAmount("");
                                                    setMobile("");
                                                    setLeadId("");
                                                    setAssignedToFilter("");
                                                    setLenderId("");
                                                    setSource("");
                                                    setStatus("");
                                                    setPage(1);
                                                    setLeads([]);
                                                    setError(null);
                                                    setNoDataMessage(null);
                                                    // Clear multiple values
                                                    setMobileNumbers([]);
                                                    setLeadIds([]);
                                                    setCurrentMobileInput('');
                                                    setCurrentLeadIdInput('');
                                                    // Clear saved search results when switching to All Filters mode
                                                    sessionStorage.removeItem('quickSearchResults');
                                                    sessionStorage.removeItem('quickSearchParams');
                                                }}
                                                style={{
                                                    padding: '10px 20px',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    backgroundColor: !isSearchMode ? '#fff' : 'transparent',
                                                    color: !isSearchMode ? '#1ab394' : '#666',
                                                    fontWeight: !isSearchMode ? '600' : '500',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: !isSearchMode ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (isSearchMode) {
                                                        e.target.style.backgroundColor = '#e8e8e8';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (isSearchMode) {
                                                        e.target.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <i className="fa fa-filter"></i>
                                                <span>All Filters</span>
                                            </button>
                                            
                                            {/* Quick Search tab - only show if user has permission */}
                                            {canUseQuickSearch && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsSearchMode(true);
                                                        setHasSearched(false);
                                                        setStartDate("");
                                                        setEndDate("");
                                                        setProductType("");
                                                        setLoanAmount("");
                                                        setMobile("");
                                                        setLeadId("");
                                                        setAssignedToFilter("");
                                                        setLenderId("");
                                                        setSource("");
                                                        setStatus("");
                                                        setPage(1);
                                                        setLeads([]);
                                                        setError(null);
                                                        setNoDataMessage(null);
                                                        // Clear multiple values
                                                        setMobileNumbers([]);
                                                        setLeadIds([]);
                                                        setCurrentMobileInput('');
                                                        setCurrentLeadIdInput('');
                                                        // Clear any existing saved search results when switching to Quick Search mode
                                                        sessionStorage.removeItem('quickSearchResults');
                                                        sessionStorage.removeItem('quickSearchParams');
                                                    }}
                                                    style={{
                                                        padding: '10px 20px',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        backgroundColor: isSearchMode ? '#fff' : 'transparent',
                                                        color: isSearchMode ? '#1ab394' : '#666',
                                                        fontWeight: isSearchMode ? '600' : '500',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: isSearchMode ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSearchMode) {
                                                            e.target.style.backgroundColor = '#e8e8e8';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSearchMode) {
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }
                                                    }}
                                                >
                                                    <i className="fa fa-search"></i>
                                                    <span>Quick Search</span>
                                                </button>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <form onSubmit={handleSubmit}>
                                                {isSearchMode ? (
                                                    // Search Mode - Only Mobile and Lead ID with multiple values support
                                                    <div className="row" style={{ alignItems: 'flex-start' }}>
                                                        <div className="form-group col-sm-6">
                                                            <label htmlFor="mobile" style={{ marginBottom: '8px', display: 'block' }}>Mobile Numbers:</label>
                                                            <div style={{ 
                                                                border: '1px solid #ddd', 
                                                                borderRadius: '4px', 
                                                                padding: '8px',
                                                                minHeight: '42px',
                                                                backgroundColor: '#fff',
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}>
                                                                {/* Display added mobile numbers as tags */}
                                                                {mobileNumbers.map((mobile, index) => (
                                                                    <span
                                                                        key={index}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            backgroundColor: '#1ab394',
                                                                            color: 'white',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '12px',
                                                                            fontSize: '12px',
                                                                            fontWeight: '500',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {mobile}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeMobileNumber(mobile)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: 'white',
                                                                                marginLeft: '4px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px',
                                                                                lineHeight: '1',
                                                                                padding: '0',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: '14px',
                                                                                height: '14px',
                                                                                borderRadius: '50%',
                                                                                transition: 'background-color 0.2s'
                                                                            }}
                                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                                <input
                                                                    type="text"
                                                                    id="mobile"
                                                                    value={currentMobileInput}
                                                                    onChange={(e) => {
                                                                        // Remove all non-numeric characters including all whitespace characters (spaces, tabs, newlines, etc.)
                                                                        const value = e.target.value.replace(/\D/g, '');
                                                                        const restrictedValue = value.slice(0, 10);
                                                                        setCurrentMobileInput(restrictedValue);
                                                                    }}
                                                                    onKeyDown={handleMobileKeyDown}
                                                                    placeholder="Enter mobile number and press Tab"
                                                                    style={{
                                                                        border: 'none',
                                                                        outline: 'none',
                                                                        flex: '1',
                                                                        minWidth: '200px',
                                                                        fontSize: '14px',
                                                                        padding: '4px 0'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="form-group col-sm-6">
                                                            <label htmlFor="leadId" style={{ marginBottom: '8px', display: 'block' }}>Lead IDs:</label>
                                                            <div style={{ 
                                                                border: '1px solid #ddd', 
                                                                borderRadius: '4px', 
                                                                padding: '8px',
                                                                minHeight: '42px',
                                                                backgroundColor: '#fff',
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}>
                                                                {/* Display added lead IDs as tags */}
                                                                {leadIds.map((leadId, index) => (
                                                                    <span
                                                                        key={index}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            backgroundColor: '#1ab394',
                                                                            color: 'white',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '12px',
                                                                            fontSize: '12px',
                                                                            fontWeight: '500',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {leadId}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeLeadId(leadId)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: 'white',
                                                                                marginLeft: '4px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px',
                                                                                lineHeight: '1',
                                                                                padding: '0',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: '14px',
                                                                                height: '14px',
                                                                                borderRadius: '50%',
                                                                                transition: 'background-color 0.2s'
                                                                            }}
                                                                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                                <input
                                                                    type="text"
                                                                    id="leadId"
                                                                    value={currentLeadIdInput}
                                                                    onChange={(e) => {
                                                                        // Remove all whitespace characters (spaces, tabs, newlines, etc.) and keep alphanumeric characters, hyphens, and underscores
                                                                        const cleanValue = e.target.value.replace(/\s/g, '').trim();
                                                                        setCurrentLeadIdInput(cleanValue);
                                                                    }}
                                                                    onKeyDown={handleLeadIdKeyDown}
                                                                    placeholder="Enter Lead ID and press Tab"
                                                                    style={{
                                                                        border: 'none',
                                                                        outline: 'none',
                                                                        flex: '1',
                                                                        minWidth: '200px',
                                                                        fontSize: '14px',
                                                                        padding: '4px 0'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    // Normal Mode - All filters
                                                    <>
                                                <div className="row">
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="startDate">Start Date:</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="startDate"
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="endDate">End Date:</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="endDate"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="productType">Product Type:</label>
                                                        <select
                                                            className="form-control"
                                                            id="productType"
                                                            value={productType}
                                                            onChange={(e) => setProductType(e.target.value)}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="BUSINESS">Business</option>
                                                            <option value="COMMERCIAL VEHICLE LOAN">Commercial Vehicle Loan</option>
                                                            <option value="BUSINESS LOAN">Business Loan</option>
                                                            <option value="BUSINESS EXPANSION LOAN">Business Expansion Loan</option>
                                                            <option value="TWO WHEELER LOAN">Two Wheeler Loan</option>
                                                            <option value="PERSONAL LOAN">Personal Loan</option>
                                                            <option value="INVOICE">Invoice</option>
                                                            <option value="PAYMENT">Payment</option>
                                                            <option value="LENDER PAGE">Lender Page</option>
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="loanAmount">Loan Amount:</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="loanAmount"
                                                            value={loanAmount}
                                                            onChange={(e) => setLoanAmount(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="mobile">Mobile:</label>
                                                        <div className="form-control" style={{ 
                                                            height: 'auto',
                                                            minHeight: '34px',
                                                            padding: '6px 12px',
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            {/* Display added mobile numbers as tags */}
                                                            {mobileNumbers.map((mobile, index) => (
                                                                <span
                                                                    key={index}
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        backgroundColor: '#1ab394',
                                                                        color: 'white',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '10px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '500',
                                                                        whiteSpace: 'nowrap',
                                                                        maxWidth: '80px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {mobile}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeMobileNumber(mobile)}
                                                                        style={{
                                                                            background: 'rgba(255,255,255,0.1)',
                                                                            border: 'none',
                                                                            color: 'white',
                                                                            marginLeft: '4px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px',
                                                                            lineHeight: '1',
                                                                            padding: '0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            borderRadius: '50%',
                                                                            transition: 'background-color 0.2s',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        <input
                                                            type="text"
                                                            id="mobile"
                                                                value={currentMobileInput}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                const restrictedValue = value.slice(0, 10);
                                                                setCurrentMobileInput(restrictedValue);
                                                            }}
                                                                onKeyDown={handleMobileKeyDown}
                                                                placeholder="Enter mobile number and press Tab"
                                                                style={{
                                                                    border: 'none',
                                                                    outline: 'none',
                                                                    flex: '1',
                                                                    minWidth: '100px',
                                                                    fontSize: '14px',
                                                                    padding: '2px 0',
                                                                    backgroundColor: 'transparent'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="leadId">Lead ID:</label>
                                                        <div className="form-control" style={{ 
                                                            height: 'auto',
                                                            minHeight: '34px',
                                                            padding: '6px 12px',
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            {/* Display added lead IDs as tags */}
                                                            {leadIds.map((leadId, index) => (
                                                                <span
                                                                    key={index}
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        backgroundColor: '#1ab394',
                                                                        color: 'white',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '10px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '500',
                                                                        whiteSpace: 'nowrap',
                                                                        maxWidth: '80px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {leadId}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeLeadId(leadId)}
                                                                        style={{
                                                                            background: 'rgba(255,255,255,0.1)',
                                                                            border: 'none',
                                                                            color: 'white',
                                                                            marginLeft: '4px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px',
                                                                            lineHeight: '1',
                                                                            padding: '0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            borderRadius: '50%',
                                                                            transition: 'background-color 0.2s',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        <input
                                                            type="text"
                                                            id="leadId"
                                                                value={currentLeadIdInput}
                                                                onChange={(e) => setCurrentLeadIdInput(e.target.value)}
                                                                onKeyDown={handleLeadIdKeyDown}
                                                                placeholder="Enter Lead ID and press Tab"
                                                                style={{
                                                                    border: 'none',
                                                                    outline: 'none',
                                                                    flex: '1',
                                                                    minWidth: '100px',
                                                                    fontSize: '14px',
                                                                    padding: '2px 0',
                                                                    backgroundColor: 'transparent'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="assignedToFilter">Assigned To:</label>
                                                        <select
                                                            className="form-control"
                                                            id="assignedToFilter"
                                                            value={assignedToFilter}
                                                            onChange={(e) => setAssignedToFilter(e.target.value)}
                                                        >
                                                            <option value="">Select User</option>
                                                            {users.filter(u => u.userType === 0).map(user => (
                                                                <option key={user.id} value={user.id}>
                                                                    {user.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="lenderIdFilter">Lender ID:</label>
                                                        <select
                                                            className="form-control"
                                                            id="lenderIdFilter"
                                                            value={lenderId}
                                                            onChange={(e) => setLenderId(e.target.value)}
                                                        >
                                                            <option value="">Select Lender</option>
                                                            <option value="TRUSTPAISA_001">TRUSTPAISA_001</option>
                                                            <option value="CASHE_001">CASHE_001</option>
                                                            <option value="FIMONEY_001">FIMONEY_001</option>
                                                            <option value="DWINGER_001">DWINGER_001</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="sourceFilter">Source:</label>
                                                        <select
                                                            className="form-control"
                                                            id="sourceFilter"
                                                            value={source}
                                                            onChange={(e) => setSource(e.target.value)}
                                                        >
                                                            <option value="">Select Source</option>
                                                            <option value="Direct">Direct</option>
                                                            <option value="Direct Lender Marketing">Direct Lender Marketing</option>
                                                            <option value="Whatsapp">Whatsapp</option>
                                                            <option value="Self">Self</option>
                                                            <option value="Social Media">Social Media</option>
                                                            <option value="Groups">Groups</option>
                                                        </select>
                                                    </div>
                                                    <div className="form-group col-sm-3">
                                                        <label htmlFor="statusFilter">Status:</label>
                                                        <select
                                                            className="form-control"
                                                            id="statusFilter"
                                                            value={status}
                                                            onChange={(e) => setStatus(e.target.value)}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {leadStatuses.map((statusOption) => (
                                                                <option key={statusOption} value={statusOption}>
                                                                    {statusOption}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                    </>
                                                )}
                                                <div className="row">
                                                    <div className="form-group col-sm-3" style={{ marginTop: '25px' }}>
                                                        <button type="submit" className="btn btn-primary">Filter</button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-default ml-2" 
                                                            onClick={handleClearFilter}
                                                            style={{ marginLeft: '10px' }}
                                                        >
                                                            Clear Filter
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                    <div className="row m-t-md">
                                        <div className="col-sm-6 text-left">
                                    <button onClick={generateCSV} className="btn btn-success">Download CSV</button>
                                        </div>
                                        {!isSearchMode && (
                                        <div className="col-sm-6 text-right">
                                            <button className="btn btn-primary" onClick={handleGenerateLead}>Generate New Lead</button>
                                        </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-heading">
                                    <div className="panel-tools">
                                        <a className="showhide"><i className="fa fa-chevron-up"></i></a>
                                        <a className="closebox"><i className="fa fa-times"></i></a>
                                    </div>
                                    Title
                                </div>
                                <div className="panel-body">
                                    <div className="table-responsive">
                                        <table className="table table-striped table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: '180px' }}>Created At</th>
                                                    <th style={{ minWidth: '150px' }}>Application ID</th>
                                                    <th style={{ minWidth: '150px' }}>Product Type</th>
                                                    <th style={{ minWidth: '150px' }}>Mobile</th>
                                                    <th style={{ minWidth: '150px' }}>Loan Amount</th>
                                                    <th style={{ minWidth: '150px' }}>Lead Status</th>
                                                    <th style={{ minWidth: '150px' }}>App Progress</th>
                                                    <th style={{ minWidth: '150px' }}>Remarks</th>
                                                    <th style={{ minWidth: '150px' }}>Assigned Lender</th>
                                                    <th style={{ minWidth: '150px' }}>Customer ID</th>
                                                    <th style={{ minWidth: '150px' }}>Customer Name</th>
                                                    <th style={{ minWidth: '150px' }}>Email</th>
                                                    <th style={{ minWidth: '150px' }}>Assigned To</th>
                                                    <th style={{ minWidth: '150px' }}>Source</th>
                                                    <th style={{ minWidth: '150px' }}>Follow-up Date</th>
                                                    <th style={{ minWidth: '150px' }}>Created By</th>
                                                    <th style={{ minWidth: '180px' }}>Last Updated</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(isSearchMode && !hasSearched) ? (
                                                    <tr>
                                                        <td colSpan="17" className="text-center" style={{ padding: '60px' }}>
                                                            <div>
                                                                <i className="fa fa-search fa-3x text-muted" style={{ marginBottom: '20px' }}></i>
                                                                <h4 className="text-muted">Ready to Search</h4>
                                                                <p className="text-muted">Enter a Mobile Number or Lead ID above and click Filter to search for leads.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : leads.length > 0 ? (leads.map((lead) => (
                                                    <tr key={lead.id}>
                                                        <td style={{ minWidth: '180px' }}>{lead.createdAt || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            <span 
                                                                className="text-primary" 
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px',
                                                                    transition: 'all 0.2s ease',
                                                                    fontWeight: '500'
                                                                }}
                                                                onClick={() => handleViewDetails(lead)}
                                                                onMouseEnter={(e) => {
                                                                    e.target.style.color = '#0056b3';
                                                                    e.target.style.textDecoration = 'underline';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.target.style.color = '';
                                                                    e.target.style.textDecoration = 'underline';
                                                                }}
                                                            >
                                                                <i className="fa fa-file-text-o"></i>
                                                                {lead.id || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>{lead.productType || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            {lead.mobile || 'N/A'}
                                                            {typeof lead.isMobileVerified === 'boolean' && (
                                                                <span
                                                                    title={lead.isMobileVerified ? 'Mobile Verified' : 'Mobile Not Verified'}
                                                                    style={{
                                                                        marginLeft: 8,
                                                                        color: lead.isMobileVerified ? 'green' : 'red',
                                                                        fontSize: '16px',
                                                                        verticalAlign: 'middle',
                                                                    }}
                                                                >
                                                                    <i className={`fa ${lead.isMobileVerified ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>{lead.loanAmount || 'N/A'}</td>
                                                        <td style={{ minWidth: '200px' }}>
                                                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                            {isSearchMode ? (
                                                                <span className="form-control" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', flex: 1 }}>
                                                                    {lead.leadStatus || 'N/A'}
                                                                </span>
                                                            ) : (
                                                            <select
                                                                className="form-control"
                                                                value={lead.leadStatus || ''}
                                                                onChange={(e) => handleLeadStatusChange(lead.id, e.target.value)}
                                                                    style={{ flex: 1 }}
                                                            >
                                                                {leadStatuses.map(status => (
                                                                    <option key={status} value={status}>{status}</option>
                                                                ))}
                                                            </select>
                                                            )}
                                                                {!isSearchMode && lead.lenderInfo?.lenderId === CASHE_LENDER_ID && lead.partnerCustomerId && (
                                                                    <button
                                                                        className="btn btn-sm btn-info"
                                                                        onClick={() => updateCustomerState(lead)}
                                                                        disabled={updatingCustomerState[lead.id]}
                                                                        title="Update customer state from lender"
                                                                        style={{
                                                                            minWidth: '40px',
                                                                            padding: '4px 8px',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    >
                                                                        {updatingCustomerState[lead.id] ? (
                                                                            <i className="fa fa-spinner fa-spin"></i>
                                                                        ) : (
                                                                            <i className="fa fa-refresh"></i>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ minWidth: '150px', cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
                                                            onClick={() => {
                                                                const { steps, completed } = getAppProgressSteps(lead);
                                                                setProgressModal({ open: true, steps, completed });
                                                            }}
                                                        >
                                                            {getAppProgress(lead)}
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            <span 
                                                                className="text-primary" 
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '5px'
                                                                }}
                                                                onClick={() => {
                                                                    setRemarksToDisplay(lead.remarks || '');
                                                                    setCurrentRemarksLead(lead);
                                                                    setNewRemarkText('');
                                                                    setShowRemarksModal(true);
                                                                }}
                                                            >
                                                                <i className={lead.remarks ? "fa fa-eye" : (isSearchMode ? "fa fa-eye" : "fa fa-plus")}></i>
                                                                {lead.remarks ? 'View Notes' : (isSearchMode ? 'View Notes' : 'Add Note')}
                                                            </span>
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            {lead.lenderInfo?.lenderId || 'N/A'}
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>{lead.customerId || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>{lead.customerName || lead.name || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>{lead.email || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            {isSearchMode ? (
                                                                <span className="form-control input-sm" style={{ 
                                                                    backgroundColor: '#f8f9fa', 
                                                                    border: '1px solid #dee2e6',
                                                                    minWidth: '120px',
                                                                    fontSize: '12px',
                                                                    padding: '4px 8px',
                                                                    height: '32px',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    {getUserNameById(lead.assignedTo) || 'Not Assigned'}
                                                                </span>
                                                            ) : (
                                                            <select 
                                                                className="form-control input-sm"
                                                                value={lead.assignedTo || ''}
                                                                onChange={(e) => handleAssignmentChange(lead.id, e.target.value)}
                                                                style={{ 
                                                                    minWidth: '120px',
                                                                    fontSize: '12px',
                                                                    padding: '4px 8px',
                                                                    height: '32px'
                                                                }}
                                                                title="Select user to assign this lead"
                                                            >
                                                                <option value="">Select User</option>
                                                                {users.filter(u => u.userType === 0).map(user => (
                                                                    <option key={user.id} value={user.id}>
                                                                        {user.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            )}
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>{lead.source || 'N/A'}</td>
                                                        <td style={{ minWidth: '150px' }}>
                                                            {isSearchMode ? (
                                                                <span className="form-control input-sm" style={{ 
                                                                    backgroundColor: '#f8f9fa', 
                                                                    border: '1px solid #dee2e6',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    {lead.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : 'Not Set'}
                                                                </span>
                                                            ) : (
                                                            <input
                                                                type="date"
                                                                className="form-control input-sm"
                                                                value={formatDateForInput(lead.followUpDate) || ''}
                                                                onChange={(e) => handleFollowUpDateChange(lead.id, e.target.value)}
                                                                min={new Date().toISOString().split('T')[0]}
                                                            />
                                                            )}
                                                        </td>
                                                        <td style={{ minWidth: '150px' }}>{getUserNameById(lead.createdBy)}</td>
                                                        <td style={{ minWidth: '180px' }}>{lead.lastUpdated || 'N/A'}</td>
                                                    </tr>
                                                ))) : (
                                                    <tr>
                                                        <td colSpan="16" className="text-center" style={{ padding: '40px' }}>
                                                            {noDataMessage ? (
                                                                <div>
                                                                    <i className="fa fa-search fa-2x text-muted"></i>
                                                                    <h5 className="m-t-md text-muted">{noDataMessage}</h5>
                                                                    <p className="text-muted">{isSearchMode ? 'Try adjusting your search inputs.' : 'Try adjusting your search filters or create a new lead.'}</p>
                                                                    <div>
                                                                        {!isSearchMode && (
                                                                        <button 
                                                                            className="btn btn-primary m-t-md m-r-sm" 
                                                                            onClick={handleGenerateLead}
                                                                        >
                                                                            <i className="fa fa-plus"></i> Generate New Lead
                                                                        </button>
                                                                        )}
                                                                        <button 
                                                                            className="btn btn-default m-t-md" 
                                                                            onClick={() => {
                                                                                setStartDate("");
                                                                                setEndDate("");
                                                                                setProductType("");
                                                                                setLoanAmount("");
                                                                                setMobile("");
                                                                                setSource("");
                                                                                setStatus("");
                                                                                setPage(1);
                                                                                setNoDataMessage(null);
                                                                                setHasSearched(false);
                                                                                if (!isSearchMode) {
                                                                                fetchLeads();
                                                                                }
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-refresh"></i> Clear Filters
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <i className="fa fa-inbox fa-2x text-muted"></i>
                                                                    <h5 className="m-t-md text-muted">No leads found</h5>
                                                                    <p className="text-muted">{isSearchMode ? 'Try another search.' : 'Get started by creating your first lead.'}</p>
                                                                    <div>
                                                                        {!isSearchMode && (
                                                                        <button 
                                                                            className="btn btn-primary m-t-md m-r-sm" 
                                                                            onClick={handleGenerateLead}
                                                                        >
                                                                            <i className="fa fa-plus"></i> Generate New Lead
                                                                        </button>
                                                                        )}
                                                                        {!isSearchMode && (
                                                                        <button 
                                                                            className="btn btn-default m-t-md" 
                                                                            onClick={() => {
                                                                                    if (!isSearchMode) {
                                                                                fetchLeads();
                                                                                    }
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-refresh"></i> Refresh
                                                                        </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="text-right m-t-md">
                                        <label htmlFor="recordsPerPage" className="m-r-sm">Records per page:</label>
                                        <select
                                            id="recordsPerPage"
                                            className="form-control input-sm" 
                                            style={{ display: 'inline-block', width: 'unset' }}
                                            value={recordsPerPage}
                                            onChange={(e) => {
                                                setRecordsPerPage(Number(e.target.value));
                                                setPage(1); // Reset to first page when records per page changes
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <button
                                            className="btn btn-default m-r-sm m-l-sm"
                                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {page}</span>
                                        <button
                                            className="btn btn-default m-l-sm"
                                            onClick={() => setPage((prev) => prev + 1)}
                                            disabled={!hasMoreLeads}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>


            {/* Change Status Modal */}
            {statusModal.open && (
                <div className="modal" style={{ 
                    display: 'flex', 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '500px', margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Lead Status</h5>
                                <button type="button" className="close" onClick={() => setStatusModal({ open: false, lead: null })}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <select className="form-control" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                                    {leadStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setStatusModal({ open: false, lead: null })}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleStatusSave}>Change</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Note Modal */}
            {noteModal.open && (
                <div className="modal" style={{ 
                    display: 'flex', 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '500px', margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Note</h5>
                                <button type="button" className="close" onClick={() => setNoteModal({ open: false, lead: null })}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    placeholder="Enter note/remark here..."
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                ></textarea>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => { setNoteModal({ open: false, lead: null }); setNoteText(''); }}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleNoteSave}>Save Note</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Remarks Modal - View & Add */}
            {showRemarksModal && (
                <div className="modal" style={{ 
                    display: 'flex', 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '700px', width: '90%', margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                <h5 className="modal-title" style={{ 
                                    color: '#495057', 
                                    fontWeight: '600',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <i className="fa fa-comments-o" style={{ color: '#007bff', fontSize: '20px' }}></i>
                                    <span>Notes & Remarks</span>
                                    <span style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '500',
                                        color: '#6c757d',
                                        backgroundColor: '#e9ecef',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontFamily: 'monospace'
                                    }}>
                                        Lead #{currentRemarksLead?.id}
                                    </span>
                                </h5>
                                <button 
                                    type="button" 
                                    className="close" 
                                    onClick={() => {
                                        setShowRemarksModal(false);
                                        setNewRemarkText('');
                                        setCurrentRemarksLead(null);
                                    }}
                                    style={{
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        color: '#6c757d',
                                        opacity: '0.8'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.opacity = '1';
                                        e.target.style.color = '#dc3545';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.opacity = '0.8';
                                        e.target.style.color = '#6c757d';
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                {/* Existing Notes Section */}
                                <div className="mb-3">
                                    {(() => {
                                        // Parse remarks dynamically: handle both newline-separated and nested "Previous remarks:" format
                                        const parseRemarks = (remarksStr) => {
                                            if (!remarksStr || !remarksStr.trim()) return [];
                                            
                                            // First, try splitting by newline for standard format
                                            const newlineSplit = remarksStr.split('\n').filter(entry => entry.trim().length > 0);
                                            
                                            // If we have multiple entries from newline split, return them
                                            if (newlineSplit.length > 1) {
                                                return newlineSplit;
                                            }
                                            
                                            // Handle nested format: entries separated by "Previous remarks:" pattern
                                            // Pattern: [DD/MM/YYYY, HH:MM:SS am/pm] content Previous remarks: [next timestamp]...
                                            const timestampPattern = /\[\d{2}\/\d{2}\/\d{4}, \d{1,2}:\d{2}:\d{2} (am|pm)\]/g;
                                            const entries = [];
                                            
                                            // Find all timestamp positions
                                            const timestamps = [];
                                            let match;
                                            while ((match = timestampPattern.exec(remarksStr)) !== null) {
                                                timestamps.push(match.index);
                                            }
                                            
                                            // If we found timestamps, extract entries
                                            if (timestamps.length > 0) {
                                                for (let i = 0; i < timestamps.length; i++) {
                                                    const startIdx = timestamps[i];
                                                    const endIdx = i < timestamps.length - 1 ? timestamps[i + 1] : remarksStr.length;
                                                    
                                                    let entryText = remarksStr.substring(startIdx, endIdx).trim();
                                                    
                                                    // Remove "Previous remarks: " from the end if present
                                                    entryText = entryText.replace(/\s*Previous remarks:\s*$/, '').trim();
                                                    
                                                    if (entryText) {
                                                        entries.push(entryText);
                                                    }
                                                }
                                            }
                                            
                                            // Return extracted entries if found, otherwise return newline split
                                            return entries.length > 0 ? entries : newlineSplit;
                                        };
                                        const parsedRemarks = parseRemarks(remarksToDisplay);
                                        
                                        return (
                                            <>
                                                <h6 className="text-muted mb-2">
                                                    <i className="fa fa-history"></i>
                                                    Previous Notes ({parsedRemarks.length})
                                                </h6>
                                                <div 
                                                    style={{ 
                                                        maxHeight: '200px', 
                                                        overflowY: 'auto', 
                                                        border: '1px solid #ddd', 
                                                        borderRadius: '4px',
                                                        padding: '10px',
                                                        backgroundColor: '#f9f9f9'
                                                    }}
                                                >
                                                    {parsedRemarks.length > 0 ? (
                                                        parsedRemarks.map((note, idx) => (
                                                            <div 
                                                                key={idx} 
                                                                style={{ 
                                                                    marginBottom: '12px', 
                                                                    paddingBottom: '12px', 
                                                                    borderBottom: idx === parsedRemarks.length - 1 ? 'none' : '1px solid #eee',
                                                                    whiteSpace: 'pre-wrap',
                                                                    fontSize: '14px',
                                                                    lineHeight: '1.5',
                                                                    paddingLeft: '20px',
                                                                    position: 'relative'
                                                                }}
                                                            >
                                                                <span style={{
                                                                    position: 'absolute',
                                                                    left: '0',
                                                                    top: '2px',
                                                                    color: '#007bff',
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold'
                                                                }}>•</span>
                                                                <span style={{ color: '#333' }}>{note.trim()}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-muted mb-0">No previous notes available.</p>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Add New Note Section - Only in normal mode */}
                                {!isSearchMode && (
                                <div className="mt-4">
                                    <h6 className="text-primary mb-2">
                                        <i className="fa fa-plus-circle"></i>
                                        Add New Note
                                    </h6>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter your new note/remark here..."
                                        value={newRemarkText}
                                        onChange={(e) => setNewRemarkText(e.target.value)}
                                        style={{
                                            border: '2px solid #e9ecef',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    ></textarea>
                                    <small className="text-muted">
                                        <i className="fa fa-info-circle"></i>
                                        Your note will be timestamped and added to the history above.
                                    </small>
                                </div>
                                )}
                            </div>
                            <div className="modal-footer" style={{ borderTop: '2px solid #e9ecef' }}>
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={() => {
                                        setShowRemarksModal(false);
                                        setNewRemarkText('');
                                        setCurrentRemarksLead(null);
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                    Close
                                </button>
                                {!isSearchMode && (
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleRemarksModalSave}
                                    disabled={!newRemarkText.trim()}
                                    style={{
                                        opacity: newRemarkText.trim() ? 1 : 0.6
                                    }}
                                >
                                    <i className="fa fa-save"></i>
                                    Save Note
                                </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Progress Modal */}
            {progressModal.open && (
                <div className="modal" style={{ 
                    display: 'flex', 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '400px', margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Application Progress</h5>
                                <button type="button" className="close" onClick={() => setProgressModal({ open: false, steps: [], completed: [] })}>&times;</button>
                            </div>
                            <div className="modal-body">
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {progressModal.steps.map(step => (
                                        <li key={step.key} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                background: progressModal.completed.includes(step.key) ? '#4caf50' : '#e0e0e0',
                                                color: '#fff',
                                                textAlign: 'center',
                                                lineHeight: '18px',
                                                marginRight: 10,
                                                fontWeight: 'bold',
                                                fontSize: 13
                                            }}>
                                                {progressModal.completed.includes(step.key) ? '✓' : ''}
                                            </span>
                                            <span style={{ color: progressModal.completed.includes(step.key) ? '#333' : '#aaa' }}>{step.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-default" onClick={() => setProgressModal({ open: false, steps: [], completed: [] })}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Lead Details Modal - REMOVED */}
            
            {/* Error Modal */}
            <ErrorModal
                show={!!error}
                onClose={() => setError(null)}
                onRetry={() => {
                    setError(null);
                    if (!isSearchMode) {
                    fetchLeads();
                    }
                }}
                onBackToData={handleBackToLeads}
                title="Error Loading Leads"
                message={error}
                retryButtonText="Try Again"
                backButtonText="Back to Leads"
            />

            {/* No Data Modal */}
            <NoDataModal
                show={!!noDataMessage}
                onClose={() => {
                    setNoDataMessage(null);
                    // Clear filters when popup is closed so pagination works correctly
                    setStartDate("");
                    setEndDate("");
                    setProductType("");
                    setLoanAmount("");
                    setMobile("");
                    setLeadId("");
                    setAssignedToFilter("");
                    setLenderId("");
                    setSource("");
                    setStatus("");
                    setPage(1);
                }}
                onClearFilters={handleBackToLeads}
                onCreateNew={handleGenerateLead}
                title="No Leads Found"
                message={noDataMessage}
                clearFiltersButtonText="Clear Filters"
                createButtonText="Generate New Lead"
            />
            
        </>
    );
};

export default LeadsPage;